import classes from "./styles.module.css";
import { Button, CircularProgress, TextField } from "@mui/material";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import axios from "axios";
import { path } from "../../../routes";
import { useEffect, useMemo, useState } from "react";
import Row_org from "./Row_org";
import YesNo_modal from "../Modal/YesNo_modal";
import AddOrg_modal from "../Modal/AddOrg_modal";
import EditOrg_modal from "../Modal/EditOrg_modal";

const SortableHeader = ({ column, children }) => (
  <span
    role="button"
    tabIndex={0}
    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && column.toggleSorting(column.getIsSorted() === "asc")}
    style={{ cursor: "pointer", userSelect: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
  >
    {children}
    {column.getIsSorted() === "asc" && " ↑"}
    {column.getIsSorted() === "desc" && " ↓"}
  </span>
);

const Organizations = (props) => {
  const [orgList, setOrgList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterName, setFilterName] = useState("");
  const [sorting, setSorting] = useState([{ id: "name", desc: false }]);
  const [modal, setModal] = useState(false);
  const [addNew, setAddNew] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [deleteData, setDeleteData] = useState([]);

  const loadDisplay = (x) => props.loadDisplay(x);
  const loadServis = (x) => props.loadServis && props.loadServis(x);

  const deleteOrg = (x) => {
    setDeleteData(x);
    setModal(true);
  };

  const getOrganizations = async () => {
    setLoading(true);
    setOrgList([]);
    try {
      const response = await axios.get(`${path}/getOrganizations`);
      setOrgList(response.data || []);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setAddNew(false);
    getOrganizations();
  };

  const closeEditModal = () => {
    setEditOrg(null);
    getOrganizations();
  };

  const handleEditOrg = (org) => setEditOrg(org);

  const closeModal2 = () => setModal(false);

  const deleteOrganization = async (x) => {
    try {
      await axios.delete(`${path}/deleteorganization/${x}`);
      alert("Users, Devices, ORG deleted successfully");
      getOrganizations();
      closeModal2();
    } catch (error) {
      console.error("Chyba při mazání:", error);
      closeModal2();
      alert("Mazání se nezdařilo");
    }
  };

  useEffect(() => {
    getOrganizations();
  }, []);

  const columns = useMemo(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: ({ column }) => <SortableHeader column={column}>Organizace</SortableHeader>,
        enableSorting: true,
        sortingFn: (rowA, rowB) => (rowA.original.name || "").localeCompare(rowB.original.name || "", "cs"),
      },
      { id: "actions", header: "Action buttons", enableSorting: false },
    ],
    []
  );

  const table = useReactTable({
    data: orgList,
    columns,
    state: { sorting, globalFilter: filterName },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: (row, _columnId, filterValue) => {
      const q = (filterValue || "").trim().toLowerCase();
      if (!q) return true;
      return (row.original.name || "").toLowerCase().includes(q);
    },
  });

  const headerGroup = table.getHeaderGroups()[0];
  const rows = table.getRowModel().rows;
  const colCount = headerGroup?.headers.length ?? 0;

  return (
    <>
      {!modal && !addNew && !editOrg && (
        <div className={classes.main}>
          <div className={classes.buttons}>
            <Button
              onClick={() => setAddNew(true)}
              color="primary"
              variant="contained"
              sx={{
                width: { xs: "100%", sm: "auto" },
                fontSize: { xs: "0.875rem", sm: "1rem" },
                padding: { xs: "8px 16px", sm: "10px 20px" },
              }}
            >
              Přidat organizaci
            </Button>
            <TextField
              size="small"
              placeholder="Filtrovat podle názvu..."
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              sx={{ minWidth: { xs: 160, sm: 220 } }}
              inputProps={{ "aria-label": "Filtrovat podle názvu" }}
            />
          </div>
          <div className={classes.table}>
            <div
              className={classes.tableGridWrapper}
              style={{ gridTemplateColumns: "minmax(0, 1fr) minmax(200px, auto)" }}
            >
              <div style={{ display: "contents" }}>
                {headerGroup?.headers.map((header, i) => (
                  <div
                    key={header.id}
                    className={`${classes.cellHeader} ${i === 0 ? classes.cellColFirst : ""} ${i === colCount - 1 ? classes.cellColLast : ""} ${header.column.id === "actions" ? classes.columnAct : classes.column}`}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </div>
                ))}
              </div>
              {loading && (
                <div className={classes.loader} style={{ gridColumn: "1 / -1" }}>
                  <CircularProgress size={40} />
                </div>
              )}
              {!loading && orgList.length === 0 && (
                <div className={classes.empty} style={{ gridColumn: "1 / -1" }}>
                  Žádné organizace
                </div>
              )}
              {!loading && orgList.length > 0 && rows.length === 0 && (
                <div className={classes.empty} style={{ gridColumn: "1 / -1" }}>
                  Žádný výsledek pro „{filterName.trim()}“
                </div>
              )}
              {!loading &&
                rows.map((row, rowIndex) => (
                  <Row_org
                    key={row.original.id}
                    data={row.original}
                    loadDisplay={loadDisplay}
                    loadServis={loadServis}
                    onDelete={deleteOrg}
                    onEdit={handleEditOrg}
                    renderAsGridRow
                    isLastRow={rowIndex === rows.length - 1}
                  />
                ))}
            </div>
          </div>
        </div>
      )}
      {modal && (
        <YesNo_modal
          text="Delete organization?"
          line={deleteData.name}
          id={deleteData.id}
          close={closeModal2}
          delete={deleteOrganization}
        />
      )}
      {addNew && <AddOrg_modal close={closeModal} />}
      {editOrg && <EditOrg_modal close={closeEditModal} organization={editOrg} />}
    </>
  );
};

export default Organizations;
